import { useState, useEffect } from "react";
import { UserCircle, KeyRound, Award, Download } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { useAuth, ROLE_LABELS } from "@/context/AuthContext";
import { downloadApiFile } from "@/components/shared/DocKit";
import { TwoFactorCard } from "@/components/portal/TwoFactorCard";

export default function Profile() {
  const { t, tt } = useLang();
  const { user } = useAuth();
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [saving, setSaving] = useState(false);
  const [certs, setCerts] = useState([]);
  const [selectedCert, setSelectedCert] = useState(null);

  useEffect(() => {
    api.get("/academy/certificates").then((r) => setCerts(r.data)).catch(() => {});
  }, []);

  const changePassword = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/auth/change-password", pw);
      toast.success(t("Passwort ge\u00e4ndert", "Password changed"));
      setPw({ current_password: "", new_password: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || t("Fehler beim \u00c4ndern", "Error changing password"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="profile-page">
      <div>
        <h1 className="font-display text-3xl font-semibold tracking-tight">{t("Profil", "Profile")}</h1>
      </div>

      <Card className="rounded-[16px]">
        <CardContent className="p-6 flex items-center gap-5">
          <div className="h-16 w-16 rounded-full bg-[#0B1220] text-[#D6C08D] flex items-center justify-center text-xl font-display font-semibold">
            {user?.first_name?.[0]}{user?.last_name?.[0]}
          </div>
          <div>
            <h2 className="text-lg font-semibold" data-testid="profile-name">{user?.first_name} {user?.last_name}</h2>
            <p className="text-sm text-muted-foreground">{user?.email}</p>
            <div className="mt-2 flex gap-2">
              <Badge variant="outline" className="rounded-full text-xs">{tt(ROLE_LABELS[user?.role])}</Badge>
              <Badge variant="secondary" className="rounded-full text-xs">
                {user?.onboarding_status === "completed" ? t("Onboarding abgeschlossen", "Onboarding completed") : "Onboarding"}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-[16px]">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Award className="h-4 w-4 text-[#BFA76A]" /> {t("Meine Zertifikate", "My certificates")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {certs.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("Noch keine Zertifikate. Schlie\u00dfen Sie Kurse in der Academy ab.", "No certificates yet. Complete courses in the Academy.")}</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {certs.map((c) => (
                <button key={c.id} onClick={() => setSelectedCert(c)} className="rounded-[12px] border border-border p-4 text-left hover:bg-muted/50 transition-colors" data-testid={`certificate-${c.id}`}>
                  <p className="text-sm font-medium">{tt(c.course_title)}</p>
                  <p className="mt-1 font-mono-num text-xs text-muted-foreground">{c.code}</p>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <TwoFactorCard />

      <Card className="rounded-[16px]">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-[#BFA76A]" /> {t("Passwort \u00e4ndern", "Change password")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={changePassword} className="space-y-4 max-w-sm">
            <div className="space-y-1.5">
              <Label>{t("Aktuelles Passwort", "Current password")}</Label>
              <Input type="password" required value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} className="rounded-[10px]" data-testid="profile-current-password" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("Neues Passwort (min. 10 Zeichen, Gro\u00df-/Kleinbuchstaben, Zahl)", "New password (min. 10 characters, upper/lower case, number)")}</Label>
              <Input type="password" required minLength={8} value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} className="rounded-[10px]" data-testid="profile-new-password" />
            </div>
            <Button type="submit" disabled={saving} className="rounded-[10px] bg-[#0B1220]" data-testid="profile-change-password-submit">
              {t("Passwort \u00e4ndern", "Change password")}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Dialog open={!!selectedCert} onOpenChange={(o) => !o && setSelectedCert(null)}>
        <DialogContent className="sm:max-w-md">
          {selectedCert && (
            <div className="text-center py-6" data-testid="certificate-dialog">
              <div className="mx-auto h-14 w-14 rounded-full bg-[#D6C08D]/15 flex items-center justify-center">
                <Award className="h-7 w-7 text-[#A8905A]" />
              </div>
              <p className="mt-5 text-xs uppercase tracking-[0.2em] text-muted-foreground">{t("Zertifikat", "Certificate")}</p>
              <h3 className="mt-2 font-display text-2xl font-semibold">{tt(selectedCert.course_title)}</h3>
              <p className="mt-3 text-sm text-muted-foreground">{t("Verliehen an", "Awarded to")}</p>
              <p className="text-base font-medium">{selectedCert.user_name}</p>
              <p className="mt-4 font-mono-num text-xs text-muted-foreground">
                {selectedCert.code} · {new Date(selectedCert.issued_at).toLocaleDateString()}
              </p>
              <p className="mt-6 font-display text-lg text-[#A8905A]">Eleven Academy</p>
              <Button
                className="mt-6 rounded-[10px] bg-[#0B1220]"
                onClick={async () => {
                  try {
                    await downloadApiFile(`/academy/certificates/${selectedCert.id}/pdf`, `Eleven-Zertifikat-${selectedCert.code}.pdf`);
                    toast.success(t("Zertifikat heruntergeladen", "Certificate downloaded"));
                  } catch {
                    toast.error(t("Download fehlgeschlagen", "Download failed"));
                  }
                }}
                data-testid="certificate-download-pdf"
              >
                <Download className="h-4 w-4 mr-1.5" /> {t("Als PDF herunterladen", "Download as PDF")}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
